import type { MetricBreakdownKey } from '@/lib/dashboard/metricBreakdownKeys'
import { CSV_EXPORT_MAX_ROWS } from '@/lib/export/csvConstants'

export type ReportingHelpSection = { heading: string; body: string }

const csvOrdersRevenueCap = CSV_EXPORT_MAX_ROWS.toLocaleString()

/** Dashboard main view — operator-facing; no API/metric logic. */
export function getDashboardReportingHelpSections(): ReportingHelpSection[] {
  return [
    {
      heading: 'Sigma Sense vs Shopify Admin',
      body:
        'Overview totals use orders that are already stored in Sigma Sense for the selected dates (your shop timezone). ' +
        'They are not live reads from Shopify. If Shopify Admin shows more orders, import or repair sync first—' +
        'Admin → Run Daily Jobs rebuilds saved totals from your Sigma database but does not fetch missing orders by itself ' +
        '(use Repair Orders / import when those tools are available). Compare with Sigma Sense → Orders using the same From/To dates. ' +
        'Scheduled jobs refresh daily summaries from the start of last calendar month through today so current-month dates stay in sync.',
    },
    {
      heading: 'Dashboard vs detail pages',
      body:
        'The dashboard shows totals for the dates you pick (you may see when they were last updated). ' +
        'Detail pages—metric breakdown, revenue lists, or print-friendly views—open with numbers for that same range when you visit them.',
    },
    {
      heading: '“Gross Revenue” on the dashboard',
      body:
        'This card is based on delivered revenue for the range. ' +
        'It is not the same as “Total Revenue (All),” which uses each order’s full total for orders placed in the range.',
    },
    {
      heading: 'Total Profit',
      body:
        'The card uses saved daily profit when it is available for the range. ' +
        'Otherwise it uses delivered revenue minus the main expense categories—consistent with the breakdown page for the same dates.',
    },
    {
      heading: 'In Transit',
      body:
        'This count is built from daily totals, using the day each order was placed in your shop’s timezone. ' +
        'It is not a live map of where parcels are right now.',
    },
    {
      heading: 'CSV row limits',
      body:
        `Exports from the Orders list and from Revenue details are each limited to ${csvOrdersRevenueCap} data rows. ` +
        'If you hit that cap, narrow the date range or filters and export again. Other downloads may work differently.',
    },
  ]
}

/** Revenue details page. */
export function getRevenueDetailsReportingHelpSections(): ReportingHelpSection[] {
  return [
    {
      heading: 'Dashboard vs this page',
      body:
        'Dashboard cards summarize your range. This page lists orders for the segment and dates you selected so you can review amounts order by order.',
    },
    {
      heading: 'CSV export on this page',
      body:
        `The Export CSV button here uses the same ${csvOrdersRevenueCap}-row limit as the Orders list CSV export. ` +
        'If the export fails because the list is too large, shorten the date range and try again.',
    },
  ]
}

/**
 * Metric breakdown page. Extra sections only for gross_revenue and total_profit.
 */
export function getMetricBreakdownHelpSections(metric: MetricBreakdownKey | null): ReportingHelpSection[] {
  const base: ReportingHelpSection[] = [
    {
      heading: 'Dashboard vs this page',
      body:
        'Dashboard cards reflect your selected dates. This report explains how one card’s total is built for the same period.',
    },
  ]

  if (metric === 'gross_revenue') {
    base.push({
      heading: 'This report: “Gross Revenue” card',
      body:
        'Gross Revenue is delivered revenue for the period. It is not the same figure as Total Revenue (All). ' +
        'The sections below match the Gross Revenue card.',
    })
  }

  if (metric === 'total_profit') {
    base.push({
      heading: 'This report: Total Profit',
      body:
        'Total profit for the period matches your dashboard. The report shows how that total is put together and highlights major cost categories for context.',
    })
  }

  if (metric === 'payfast_fees') {
    base.push({
      heading: 'This report: PayFast fees',
      body:
        'PayFast fees are summed from your saved daily totals for the same dates as the dashboard. ' +
        'The “All costs” card includes PayFast plus tax, courier, ads, operational, and inventory.',
    })
  }

  base.push(
    {
      heading: 'Links and print',
      body:
        'Copied report links include your shop; recipients need to sign in to this store in the app. ' +
        'Print view opens in a separate page layout meant for printing or saving as PDF.',
    },
    {
      heading: 'CSV limits (which export?)',
      body:
        `Orders list CSV and Revenue details CSV are each capped at ${csvOrdersRevenueCap} data rows. ` +
        'The Export CSV button on this breakdown page downloads this report’s table from what you already loaded—it is separate from those two order-list exports.',
    }
  )

  return base
}
